/// <reference lib="dom" />
/**
 * Run-log encoding (brief §5.4): run-length encoding of identical frames,
 * then gzip, then base64url. A raw 60Hz struct log is ~86KB for 120s; Stack's
 * log is a handful of taps — RLE collapses it to almost nothing.
 *
 * Wire format before gzip: repeated 8-byte records, little-endian:
 *   u16 runLength | u16 bits | i16 axisX | i16 axisY
 *
 * Works in both runtimes: CompressionStream in the browser, node:zlib in Node.
 * Not fence-code (gzip is I/O, not simulation) — but the RLE itself is exact.
 */

import type { InputFrame } from '@hnplay/shared';
import { isValidFrame } from '@hnplay/shared';

function framesEqual(a: InputFrame, b: InputFrame): boolean {
  return a.bits === b.bits && a.axisX === b.axisX && a.axisY === b.axisY;
}

export function packRle(frames: InputFrame[]): Uint8Array {
  const records: number[] = [];
  let i = 0;
  while (i < frames.length) {
    const f = frames[i] as InputFrame;
    let run = 1;
    while (i + run < frames.length && run < 0xffff && framesEqual(f, frames[i + run] as InputFrame)) run += 1;
    records.push(run, f.bits & 0xffff, f.axisX & 0xffff, f.axisY & 0xffff);
    i += run;
  }
  const out = new Uint8Array(records.length * 2);
  const dv = new DataView(out.buffer);
  for (let r = 0; r < records.length; r += 1) dv.setUint16(r * 2, records[r] as number, true);
  return out;
}

export function unpackRle(bytes: Uint8Array, maxFrames: number): InputFrame[] {
  if (bytes.length % 8 !== 0) throw new Error('run log: truncated RLE stream');
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const frames: InputFrame[] = [];
  for (let off = 0; off < bytes.length; off += 8) {
    const run = dv.getUint16(off, true);
    const bits = dv.getUint16(off + 2, true);
    const axisX = dv.getInt16(off + 4, true);
    const axisY = dv.getInt16(off + 6, true);
    if (run === 0) throw new Error('run log: zero-length run');
    if (!isValidFrame(bits, axisX, axisY)) throw new Error('run log: invalid frame');
    if (frames.length + run > maxFrames) throw new Error('run log: exceeds tick budget');
    for (let k = 0; k < run; k += 1) frames.push({ bits, axisX, axisY });
  }
  return frames;
}

interface NodeZlib {
  gzipSync(data: Uint8Array): Uint8Array;
  gunzipSync(data: Uint8Array, opts?: { maxOutputLength?: number }): Uint8Array;
}

/** Computed specifier: neither TS nor Vite resolves it; Node does at runtime. */
async function nodeZlib(): Promise<NodeZlib> {
  const specifier = 'node' + ':zlib';
  return (await import(/* @vite-ignore */ specifier)) as NodeZlib;
}

async function gzipBytes(data: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream !== 'undefined') {
    const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }
  return new Uint8Array((await nodeZlib()).gzipSync(data));
}

async function gunzipBytes(data: Uint8Array, maxBytes: number): Promise<Uint8Array> {
  if (typeof DecompressionStream !== 'undefined') {
    const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
    const out = new Uint8Array(await new Response(stream).arrayBuffer());
    if (out.length > maxBytes) throw new Error('run log: decompressed size exceeds limit');
    return out;
  }
  return new Uint8Array((await nodeZlib()).gunzipSync(data, { maxOutputLength: maxBytes }));
}

// btoa/atob are globals in BOTH runtimes (browsers, and Node since v16).
function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i] as number);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/** frames → RLE → gzip → base64url */
export async function encodeRunLog(frames: InputFrame[]): Promise<string> {
  return toBase64Url(await gzipBytes(packRle(frames)));
}

/** base64url → gunzip → RLE-expand. Caps both decompressed bytes and frame count. */
export async function decodeRunLog(encoded: string, maxFrames: number): Promise<InputFrame[]> {
  const rle = await gunzipBytes(fromBase64Url(encoded), maxFrames * 8 + 64);
  return unpackRle(rle, maxFrames);
}